import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as THREE from 'three';
import { LoadingManager } from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';

const assetBase = (() => {
  const publicUrl = process.env.PUBLIC_URL || '';
  if (!publicUrl) {
    return '/assets/';
  }
  return publicUrl.endsWith('/') ? `${publicUrl}assets/` : `${publicUrl}/assets/`;
})();

const assetPath = (relative: string) => `${assetBase}${relative}`;

type Assets = {
  models: Record<string, any>;
  textures: Record<string, any>;
  cubemaps: Record<string, any>;
  sounds: Record<string, any>;
};

type AssetToLoad =
  | { type: 'model' | 'texture' | 'fbx' | 'sound'; name: string; path: string }
  | { type: 'cubemap'; name: string; paths: string[] };

class AssetManager {
  static assets: Assets = {
    models: {},
    textures: {},
    cubemaps: {},
    sounds: {},
  };

  static assetsToLoad: AssetToLoad[] = [
    { type: 'model', name: 'CompleteScene', path: assetPath('models/complete_scene_model.glb') },
    { type: 'model', name: 'NOCompleteScene', path: assetPath('models/no_complete_scene_model.glb') },
    { type: 'model', name: 'OnlyTurret', path: assetPath('models/OnlyTurret.glb') },
    { type: 'model', name: 'TankBottom', path: assetPath('models/TankBottom.glb') },
    { type: 'texture', name: 'textureA', path: assetPath('models/T90/T90A.png') },
    { type: 'texture', name: 'textureB', path: assetPath('models/T90/T90B.png') },
    { type: 'texture', name: 'textureC', path: assetPath('models/T90/T90C.png') },
    { type: 'texture', name: 'textureD', path: assetPath('models/T90/T90D.png') },
    { type: 'texture', name: 'textureE', path: assetPath('models/T90/T90E.png') },
    { type: 'texture', name: 'textureF', path: assetPath('models/T90/T90F.png') },
    { type: 'texture', name: 'textureG', path: assetPath('models/T90/T90G.png') },
    { type: 'texture', name: 'brickColorMap', path: assetPath('textures/brick/Bricks085_1K-JPG_Color.jpg') },
    { type: 'texture', name: 'brickNormalMap', path: assetPath('textures/brick/Bricks085_1K-JPG_NormalGL.jpg') },
    { type: 'texture', name: 'brickRoughnessMap', path: assetPath('textures/brick/Bricks085_1K-JPG_Roughness.jpg') },
    { type: 'texture', name: 'brickAOMap', path: assetPath('textures/brick/Bricks085_1K-JPG_AmbientOcclusion.jpg') },
    { type: 'texture', name: 'brickDisplacementMap', path: assetPath('textures/brick/Bricks085_1K-JPG_Displacement.jpg') },
    { type: 'texture', name: 'rustyMetalTexture', path: assetPath('textures/rustyMetalTexture.jpg') },
    { type: 'texture', name: 'grassyStoneTexture', path: assetPath('textures/grassyStoneTexture.jpg') },
    { type: 'fbx', name: 'tankModel', path: assetPath('models/T90/T90_1.fbx') },
    {
      type: 'cubemap',
      name: 'alaSkyboxTexture',
      paths: [
        assetPath('cubemaps/ala_world/posx.jpg'),
        assetPath('cubemaps/ala_world/negx.jpg'),
        assetPath('cubemaps/ala_world/posy.jpg'),
        assetPath('cubemaps/ala_world/negy.jpg'),
        assetPath('cubemaps/ala_world/posz.jpg'),
        assetPath('cubemaps/ala_world/negz.jpg'),
      ],
    },
    {
      type: 'cubemap',
      name: 'fantasySkybox',
      paths: [
        assetPath('cubemaps/ala_world/posx.jpg'),
        assetPath('cubemaps/ala_world/negx.jpg'),
        assetPath('cubemaps/ala_world/posy.jpg'),
        assetPath('cubemaps/ala_world/negy.jpg'),
        assetPath('cubemaps/ala_world/posz.jpg'),
        assetPath('cubemaps/ala_world/negz.jpg'),
      ],
    },
        {
      type: 'cubemap',
      name: 'garageSkybox',
      paths: [
        assetPath('cubemaps/garage/posx.jpg'),
        assetPath('cubemaps/garage/negx.jpg'),
        assetPath('cubemaps/garage/posy.jpg'),
        assetPath('cubemaps/garage/negy.jpg'),
        assetPath('cubemaps/garage/posz.jpg'),
        assetPath('cubemaps/garage/negz.jpg'),
      ],
    },
        {
      type: 'cubemap',
      name: 'sunsetSkybox',
      paths: [
        assetPath('cubemaps/sunset/posx.jpg'),
        assetPath('cubemaps/sunset/negx.jpg'),
        assetPath('cubemaps/sunset/posy.jpg'),
        assetPath('cubemaps/sunset/negy.jpg'),
        assetPath('cubemaps/sunset/posz.jpg'),
        assetPath('cubemaps/sunset/negz.jpg'),
      ],
    },
    { type: 'sound', name: 'shotSound', path: assetPath('sounds/hit.mp3') },
    { type: 'sound', name: 'explosionSound', path: assetPath('sounds/bong_only.mp3') },
    { type: 'sound', name: 'tankFireSound', path: assetPath('sounds/explosions/expl1_best.mp3') },
    { type: 'sound', name: 'newBaretLaudSound', path: assetPath('sounds/explosions/new_baret_laud.mp3') },
    { type: 'sound', name: 'newBaretSound', path: assetPath('sounds/explosions/new_baret.mp3') },
  ];

  static audioListener = new THREE.AudioListener();
  static onLoadCallback: null | (() => void) = null;
  static loadingManager = AssetManager.createLoadingManager();

  static setOnLoadCallback(callback: () => void) {
    this.onLoadCallback = callback;
  }

  static createLoadingManager() {
    const loadingManager = new LoadingManager();
    loadingManager.onStart = (url, itemsLoaded, itemsTotal) => {
      console.log(`Started loading file: ${url}. Loaded ${itemsLoaded} of ${itemsTotal} files.`);
    };
    loadingManager.onError = (url) => {
      console.error(`There was an error loading ${url}`);
    };
    return loadingManager;
  }

  static async preloadAssets() {
    const loadPromises = this.assetsToLoad.map((asset) => {
      const loadFunctions: Record<string, (name: string, path: any) => Promise<any>> = {
        model: this.loadModel.bind(this),
        texture: this.loadTexture.bind(this),
        cubemap: this.loadCubeTexture.bind(this),
        fbx: this.loadFBXModel.bind(this),
        sound: this.loadSound.bind(this),
      };
      const loadFunction = loadFunctions[(asset as any).type];
      if (loadFunction) {
        // @ts-ignore path vs paths handled below
        return loadFunction((asset as any).name, (asset as any).path || (asset as any).paths);
      } else {
        console.warn(`Unknown asset type: ${(asset as any).type}`);
        return Promise.resolve();
      }
    });
    try {
      await Promise.all(loadPromises);
      console.log('All assets preloaded successfully.');
      if (this.onLoadCallback) this.onLoadCallback();
    } catch (error) {
      console.error('Error preloading assets:', error);
      throw error;
    }
  }

  static async loadModel(name: string, url: string) {
    if (this.assets.models[name]) return this.assets.models[name];
    const model = await new Promise<any>((resolve, reject) => {
      const loader = new GLTFLoader(this.loadingManager);
      loader.load(url, (gltf) => resolve(gltf), undefined, reject);
    });
    this.assets.models[name] = { scene: model.scene, animations: model.animations };
    return model;
  }

  static async loadTexture(name: string, url: string) {
    if (this.assets.textures[name]) return this.assets.textures[name];
    const texture = await new Promise<any>((resolve, reject) => {
      const loader = new (THREE as any).TextureLoader(this.loadingManager);
      loader.load(url, (tex: any) => resolve(tex), undefined, reject);
    });
    this.assets.textures[name] = texture;
    return texture;
  }

  static async loadCubeTexture(name: string, paths: string[]) {
    if (this.assets.cubemaps[name]) return this.assets.cubemaps[name];
    const cubeTexture = await new Promise<any>((resolve, reject) => {
      const loader = new (THREE as any).CubeTextureLoader(this.loadingManager);
      loader.load(paths, (tex: any) => resolve(tex), undefined, reject);
    });
    this.assets.cubemaps[name] = cubeTexture;
    return cubeTexture;
  }

  static async loadFBXModel(name: string, url: string) {
    if (this.assets.models[name]) return this.assets.models[name];
    const fbxModel = await new Promise<any>((resolve, reject) => {
      const loader = new FBXLoader(this.loadingManager);
      loader.load(url, (object) => resolve(object), undefined, reject);
    });
    this.assets.models[name] = fbxModel;
    return fbxModel;
  }

  static async loadSound(name: string, path: string) {
    if (this.assets.sounds[name]) return this.assets.sounds[name];
    const sound = await new Promise<any>((resolve, reject) => {
      const audioLoader = new (THREE as any).AudioLoader();
      audioLoader.load(
        path,
        (buffer: any) => {
          const audio = new (THREE as any).Audio(this.audioListener || new (THREE as any).AudioListener());
          audio.setBuffer(buffer);
          audio.setVolume(1);
          this.assets.sounds[name] = audio;
          resolve(audio);
        },
        undefined,
        reject
      );
    });
    return sound;
  }

  static getAnimations(name: string) {
    if (!this.assets.models[name]) {
      throw new Error(`Model ${name} not loaded in AssetManager, Not in: ${Object.keys(this.assets.models)}`);
    }
    return this.assets.models[name].animations;
  }

  static getModel(name: string) {
    if (!this.assets.models[name]) {
      throw new Error(`Model ${name} not loaded in AssetManager, Not in: ${Object.keys(this.assets.models)}`);
    }
    const model = this.assets.models[name].scene ?? this.assets.models[name];
    return model.clone();
  }

  static getTexture(name: string) {
    if (!this.assets.textures[name]) {
      throw new Error(`Texture ${name} not loaded in AssetManager, Not in: ${Object.keys(this.assets.textures)}`);
    }
    return this.assets.textures[name];
  }

  static getCubeTexture(name: string) {
    if (!this.assets.cubemaps[name]) {
      throw new Error(`Cubemap ${name} not loaded in AssetManager, Not in: ${Object.keys(this.assets.cubemaps)}`);
    }
    return this.assets.cubemaps[name];
  }

  static getSound(name: string) {
    if (!this.assets.sounds[name]) {
      throw new Error(`Sound "${name}" not loaded in AssetManager.`);
    }
    return this.assets.sounds[name];
  }

  static getTextureCount() {
    return Object.keys(this.assets.textures).length;
  }

  static getLoadedAssetsInfo() {
    return {
      textures: Object.keys(this.assets.textures),
      models: Object.keys(this.assets.models),
      cubemaps: Object.keys(this.assets.cubemaps),
      sounds: Object.keys(this.assets.sounds),
      textureCount: this.getTextureCount(),
    };
  }

  static playSound(name: string) {
    const sound = this.assets.sounds[name];
    if (!sound) {
      console.warn(`Sound "${name}" is not loaded in AssetManager.`);
      return;
    }
    const newSound = new (THREE as any).Audio(this.audioListener);
    newSound.setBuffer(sound.buffer);
    newSound.setVolume(sound.getVolume());
    newSound.play();
    newSound.source.onended = () => {
      newSound.disconnect();
    };
  }

  static clear() {
    for (const tex of Object.values(this.assets.textures)) {
      tex && tex.dispose && tex.dispose();
    }
    for (const cube of Object.values(this.assets.cubemaps)) {
      cube && cube.dispose && cube.dispose();
    }
    for (const model of Object.values(this.assets.models)) {
      const root = model.scene ?? model;
      if (root && root.traverse) {
        root.traverse((obj: any) => {
          if (obj.geometry && obj.geometry.dispose) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach((mat: any) => mat && mat.dispose && mat.dispose());
            } else {
              obj.material.dispose && obj.material.dispose();
            }
          }
        });
      }
    }
    for (const sound of Object.values(this.assets.sounds)) {
      if (sound && sound.stop) sound.stop();
      if (sound && sound.disconnect) sound.disconnect();
    }
    this.assets.models = {};
    this.assets.textures = {};
    this.assets.cubemaps = {};
    this.assets.sounds = {};
  }

  static forceWebGLReset() {
    this.clear();
  }
}

export default AssetManager;
